import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Copy, X, Plus } from 'lucide-react';
import { licenseApi, companyApi, Company, formatApiError } from '../services/api.ts';

// ========== Reusable Multi-select Tag Input ==========
// Supports multiple selections from a fixed option list AND user-defined custom tags.
// Value stored as comma-separated string (backward compatible with single-valued legacy data).
interface MultiSelectTagInputProps {
  options: string[];
  value: string; // comma-separated string
  onChange: (next: string) => void;
  placeholder?: string;
  name?: string;
}
function MultiSelectTagInput({ options, value, onChange, placeholder = 'Select or type to add...', name }: MultiSelectTagInputProps) {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [customOptSet, setCustomOptSet] = useState<Set<string>>(new Set());
  const wrapRef = useRef<HTMLDivElement>(null);

  const items = (value || '').split(',').map(s => s.trim()).filter(Boolean);
  const itemSet = new Set(items.map(s => s.toLowerCase()));

  // Merge canonical options + custom options + current-selected custom tokens
  const allOpts = Array.from(new Set([
    ...options,
    ...Array.from(customOptSet),
    ...items.filter(it => !options.includes(it))
  ]));

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addItem = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    if (itemSet.has(t.toLowerCase())) return;
    const next = [...items, t].join(',');
    onChange(next);
    // remember as custom option if not in canonical list
    if (!options.some(o => o.toLowerCase() === t.toLowerCase())) {
      setCustomOptSet(prev => {
        if (prev.has(t)) return prev;
        const ns = new Set(prev); ns.add(t); return ns;
      });
    }
    setInputVal('');
  };

  const removeItem = (idx: number) => {
    const next = items.filter((_, i) => i !== idx).join(',');
    onChange(next);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      if (inputVal.trim()) {
        e.preventDefault();
        addItem(inputVal);
      }
    } else if (e.key === 'Backspace' && !inputVal && items.length) {
      e.preventDefault();
      removeItem(items.length - 1);
    }
  };

  const filteredOpts = allOpts.filter(o =>
    !itemSet.has(o.toLowerCase()) &&
    (!inputVal || o.toLowerCase().includes(inputVal.toLowerCase()))
  );

  return (
    <div className="relative" ref={wrapRef}>
      <input type="hidden" name={name} value={value} />
      <div
        onClick={() => { setOpen(true); wrapRef.current?.querySelector('input[type="text"]')?.focus(); }}
        className="min-h-[42px] flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-slate-300 rounded-lg bg-white cursor-text hover:border-slate-400 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent"
      >
        {items.map((it, idx) => (
          <span key={`${it}-${idx}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-xs font-medium">
            {it}
            <button type="button" onClick={(e) => { e.stopPropagation(); removeItem(idx); }}
              className="hover:bg-blue-200 rounded p-0.5 text-blue-600">
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputVal}
          onChange={(e) => { setInputVal(e.target.value); setOpen(true); }}
          onKeyDown={onKey}
          onFocus={() => setOpen(true)}
          placeholder={items.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] outline-none border-0 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 px-1 py-0.5"
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
          {filteredOpts.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">
              {inputVal.trim() ? (
                <button
                  type="button"
                  onClick={() => addItem(inputVal)}
                  className="w-full text-left px-2 py-1 rounded hover:bg-blue-50 text-blue-600 font-medium">
                  <Plus size={12} className="inline mr-1" /> 新增「{inputVal.trim()}」
                </button>
              ) : 'No options'}
            </div>
          ) : (
            <>
              {filteredOpts.map(o => (
                <button
                  key={o}
                  type="button"
                  onClick={() => addItem(o)}
                  className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700">
                  {o}
                </button>
              ))}
              {inputVal.trim() && !filteredOpts.some(o => o.toLowerCase() === inputVal.trim().toLowerCase()) && (
                <button
                  type="button"
                  onClick={() => addItem(inputVal)}
                  className="w-full text-left px-3 py-1.5 text-sm border-t border-slate-100 text-blue-600 hover:bg-blue-50 font-medium">
                  <Plus size={12} className="inline mr-1" /> 新增「{inputVal.trim()}」
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function LicenseForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;
  const [licenseDbId, setLicenseDbId] = useState<number | null>(null);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companySuggestions, setCompanySuggestions] = useState<Company[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [certSuggestions, setCertSuggestions] = useState<string[]>([]);
  const [showCertSuggestions, setShowCertSuggestions] = useState(false);
  const companyInputRef = useRef<HTMLInputElement>(null);
  const certInputRef = useRef<HTMLInputElement>(null);
  const [existingLicenses, setExistingLicenses] = useState<any[]>([]);
  const [existingLicenseRef, setExistingLicenseRef] = useState<{certificate_name: string, location: string} | null>(null);
  
  // 下拉选项
  const renewalTypeOptions = ['Annually', 'Quarterly', 'Semi-Annually', '2-Year', '3-Year', '4-Year', '5-Year', '10-Year'];
  const levelOptions = ['Country', 'Federal', 'State', 'County', 'City'];
  const categoryOptions = [
    'Alarm Permit',
    'Business License',
    'Carb',
    'DBA',
    'Egg',
    'Energy',
    'Fire',
    'Food Facility',
    'Hazardous',
    'Health Permit',
    'UCR',
    'USDOT',
    'IRP',
    'Warehouse',
    'Wastewater',
    'Wholesale',
    'Wildlife and Fishery',
    'Recycle',
    '公司登記基礎',
    'Payroll Tax Acct',
    'Produce',
    'Public Cold Storage',
    'Registered Agent',
    'Restaurant',
    'Scale',
    'Seller Permit',
    'Shellfish',
    'Trademark',
    'Trucking',
    'Hotel',
    'Insurance',
    'Liquor',
    'Live Seafood',
    'Meat and Poultry',
    'Milk and Dairy',
    'Motor Carrier Permit',
    'Occupancy',
    'Organic',
  ];
  const statusOptions = [
    'Active',
    'Renewing',
    'Applying',
    'Cancelling',
    'Cancelled',
    '待Cancel',
    '待變更',
    'HOLD',
    '待Apply',
    'Deleted'
  ];
  const CANONICAL_RENEWAL_METHOD_OPTIONS = ['Online', 'Mail Out', '-'];
  const CANONICAL_RENEWAL_GUIDE_OPTIONS = ['主動跟進', '收到NOTICE後跟進', '收到INVOICE後跟進', '-'];
  const CANONICAL_RENEWAL_INFO_OPTIONS = [
    'Alarm Contact',
    'Coverage Form',
    'Driver Qty',
    'Egg Mill Fee Letter',
    'Egg Sales Qty (30 Dozens/Case)',
    'Electric Usage',
    'Employee Qty',
    'Gross Receipt',
    'Hazard Material',
    'Logo Speciman',
    'PM Service Record',
    'Scale Qty',
    'Sq Ft',
    'Third Party Certificate',
    'Total Miles',
    'Truck Qty',
  ];

  const renewalMethodOptions = CANONICAL_RENEWAL_METHOD_OPTIONS;
  const renewalGuideOptions = CANONICAL_RENEWAL_GUIDE_OPTIONS;
  const renewalInfoOptions = CANONICAL_RENEWAL_INFO_OPTIONS;
  const noticeInvoiceOptions = [
    'Notice - Email', 'Notice - Mails',
    'Invoice - Email', 'Invoice - Mails',
    'Notice/Invoice - Mails', '-'
  ];
  const followUpOptions = ['Post', 'Keep'];
  const paymentMethodOptions = [
    'Credit Card', 'Amex', 'Master', 'eCheck', 'Check', 'Debit Card', '第三方公司跟進'
  ];
  const stateOptions = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    'DC'
  ];
  
  // 复制功能状态
  const [selectedLicenseToCopy, setSelectedLicenseToCopy] = useState<number | null>(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [fieldsToCopy, setFieldsToCopy] = useState<string[]>([]);
  const [licenseSearchTerm, setLicenseSearchTerm] = useState('');
  
  // 可复制的字段列表
  const copyableFields = [
    { key: 'location', label: t('license.location') },
    { key: 'state', label: t('license.state') },
    { key: 'level', label: t('license.level') },
    { key: 'department', label: t('license.department') },
    { key: 'category', label: t('license.category') },
    { key: 'certificate_name', label: t('license.certificateName') },
    { key: 'renewal_type', label: t('license.renewalType') },
    { key: 'renewal_method', label: t('license.renewalMethod') },
    { key: 'renewal_guide', label: t('license.renewalGuide') },
    { key: 'renewal_info', label: t('license.renewalInfoField') },
    { key: 'info_period', label: t('license.infoPeriod') },
    { key: 'info_provider', label: t('license.infoProvider') },
    { key: 'notice_invoice', label: t('license.noticeInvoice') },
    { key: 'website', label: t('license.website') },
    { key: 'account', label: t('license.account') },
    { key: 'password_pin', label: t('license.passwordPin') },
    { key: 'contact_person', label: t('license.contactPerson') },
    { key: 'phone_number', label: t('license.phoneNumber') },
    { key: 'contact_email', label: t('license.contactEmail') },
  ];
  
  const [formData, setFormData] = useState({
    company_name: '',
    location: '',
    state: '',
    level: '',
    department: '',
    category: '',
    license_type: '',
    certificate_name: '',
    license_no: '',
    registration_date: '',
    renewal_type: '',
    expiration_date: '',
    renewal_date: '',
    required_status: '',
    status: 'Active',
    renewal_fee: '',
    conv_fee: '',
    payment_method: '',
    renewal_method: '',
    renewal_guide: '',
    renewal_info: '',
    info_period: '',
    info_provider: '',
    notice_invoice: '',
    website: '',
    account: '',
    password_pin: '',
    questions: '',
    contact_person: '',
    phone_number: '',
    contact_email: '',
    follow_up: '',
    follow_up_date: '',
    cancelled_date: '',
    remark: '',
    deletion_reason: '',
    fee_comment: '',
    external_coin: '',
    internal_coin: '',
    qtr: '',
  });

  const ONE_DAY_MS_FORM = 86400000;

  const parseDateStr = (d: string | undefined | null): number | null => {
    if (!d || d.trim() === '') return null;
    const s = d.split('T')[0].slice(0, 10);
    const p = s.split('-').map(Number);
    if (p.some(isNaN)) return null;
    return Date.UTC(p[0], p[1] - 1, p[2]);
  };

  const formatDateStr = (utcMs: number | null): string => {
    if (!utcMs) return '';
    const d = new Date(utcMs);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };

  useEffect(() => {
    fetchCompanies();
    fetchExistingLicenses();
    if (isEdit && id) {
      const parsedId = parseInt(id, 10);
      if (!Number.isFinite(parsedId) || !Number.isInteger(parsedId) || parsedId <= 0) {
        console.error('[LicenseForm] Edit entry blocked — URL id is invalid:', JSON.stringify(id), 'parsedId=', parsedId);
        alert('❌ Cannot open edit form: The license ID in the URL is missing or invalid. You will be redirected back to the License list.');
        navigate('/license');
        return;
      }
      fetchLicense(parsedId);
    } else {
      // For new licenses, no default follow_up_date needed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  useEffect(() => {
    // No auto-calculation for follow_up_date
  }, [formData.renewal_date, formData.expiration_date, formData.status]);

  const fetchCompanies = async () => {
    try {
      const response = await companyApi.getAll();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchExistingLicenses = async () => {
    try {
      const response = await licenseApi.getAll();
      setExistingLicenses(response.data);
    } catch (error) {
      console.error('Failed to fetch existing licenses:', error);
    }
  };

  const smartFillForm = (fieldName: string, value: string) => {
    if (!value || isEdit) return;

    // 检查是否有其他仓库有相同 certificate_name 的 license（用于首次续期判断）
    if (fieldName === 'certificate_name' && value) {
      const sameCertLicenses = existingLicenses.filter(l => 
        l.certificate_name && l.certificate_name.toLowerCase() === value.toLowerCase() &&
        l.location && formData.location && l.location !== formData.location
      );
      
      if (sameCertLicenses.length > 0) {
        setExistingLicenseRef({
          certificate_name: sameCertLicenses[0].certificate_name,
          location: sameCertLicenses[0].location
        });
      } else {
        setExistingLicenseRef(null);
      }
    }

    // 根据公司和证书名称匹配现有数据
    const matches = existingLicenses.filter(license => {
      const companyMatch = license.company_name && formData.company_name && 
        license.company_name.toLowerCase() === formData.company_name.toLowerCase();
      const certMatch = license.certificate_name && formData.certificate_name &&
        license.certificate_name.toLowerCase() === formData.certificate_name.toLowerCase();
      const stateMatch = license.state && formData.state &&
        license.state.toLowerCase() === formData.state.toLowerCase();
      
      return companyMatch || (companyMatch && certMatch) || (companyMatch && stateMatch);
    });

    if (matches.length > 0) {
      const matchedLicense = matches[0];
      const newFormData: any = {};
      
      // 自动填充字段（只填充未填写的字段）
      const fieldsToFill = [
        'level', 'category', 'renewal_type', 'status', 'renewal_fee', 'conv_fee',
        'payment_method', 'renewal_method', 'renewal_guide', 'website', 'contact_person', 'phone_number', 
        'contact_email', 'follow_up', 'renewal_info', 'info_period',
        'info_provider', 'notice_invoice', 'fee_comment'
      ];

      fieldsToFill.forEach(field => {
        if (!formData[field] && matchedLicense[field]) {
          newFormData[field] = matchedLicense[field];
        }
      });

      if (Object.keys(newFormData).length > 0) {
        setFormData(prev => ({ ...prev, ...newFormData }));
      }
    }
  };

  const isValidPositiveInteger = (n: unknown): n is number => {
    return typeof n === 'number' && Number.isFinite(n) && Number.isInteger(n) && n > 0;
  };

  const fetchLicense = async (licenseId: number) => {
    if (!isValidPositiveInteger(licenseId)) {
      console.error('[LicenseForm] fetchLicense skipped — invalid licenseId:', licenseId);
      setLicenseDbId(null);
      return;
    }
    try {
      const response = await licenseApi.getById(licenseId);
      const license = response.data;
      // —— Debug: backend lookup result (raw response from DB via API) ——
      console.groupCollapsed('%c[LicenseForm] fetchLicense → backend lookup result (GET /api/license/:id response.data)', 'color:#0369a1;font-weight:bold;');
      console.log('requested id (from URL param):', licenseId);
      console.log('backend lookup result keys:', Object.keys(license || {}));
      console.log('backend lookup result — id:', license?.id, 'company_name:', license?.company_name, 'certificate_name:', license?.certificate_name, 'status:', license?.status);
      console.groupEnd();
      console.log('Fetched license data:', license);
      console.log('License status:', license.status);

      if (license && isValidPositiveInteger(license.id)) {
        // 🔑 IMPORTANT: save id is ALWAYS derived from the COMPLETE DB record returned here — NOT from URL, NOT from UI merged/filtered objects
        setLicenseDbId(license.id);
        // —— Debug: selected record object (the full DB record chosen to be edited) ——
        console.groupCollapsed('%c[LicenseForm] Edit → selected record object (from full DB lookup, loaded into form)', 'color:#16a34a;font-weight:bold;');
        console.log('license_id (primary key, FROM DB RECORD):', license.id);
        console.log('selected record object (full DB row):', license);
        console.log('licenseDbId state will be set to:', license.id, '— this is the SOLE identifier used for update/save');
        console.groupEnd();
      } else {
        console.error('[LicenseForm] Fetched license has missing/invalid id:', license?.id);
        setLicenseDbId(null);
      }

      setFormData({
        company_name: license.company_name || '',
        location: license.location || '',
        state: license.state || '',
        level: license.level || '',
        department: license.department || '',
        category: license.category || '',
        certificate_name: license.certificate_name || '',
        license_no: license.license_no || '',
        registration_date: license.registration_date || '',
        renewal_type: license.renewal_type || '',
        expiration_date: license.expiration_date || '',
        renewal_date: license.renewal_date || '',
        required_status: license.required_status || '',
        status: license.status || 'Active',
        renewal_fee: license.renewal_fee ?? '',
        conv_fee: license.conv_fee ?? '',
        payment_method: license.payment_method || '',
        renewal_method: license.renewal_method || '',
        renewal_guide: license.renewal_guide || '',
        renewal_info: license.renewal_info || '',
        info_period: license.info_period || '',
        info_provider: license.info_provider || '',
        notice_invoice: license.notice_invoice || '',
        website: license.website || '',
        account: license.account || '',
        password_pin: license.password_pin || '',
        questions: license.questions || '',
        contact_person: license.contact_person || '',
        phone_number: license.phone_number || '',
        contact_email: license.contact_email || '',
        follow_up: license.follow_up || '',
        follow_up_date: license.follow_up_date || '',
        cancelled_date: license.cancelled_date || '',
        remark: license.remark || '',
        deletion_reason: (license as any).deleted_reason || (license as any).deletion_reason || '',
        fee_comment: license.fee_comment || '',
        external_coin: license.external_coin || '',
        internal_coin: license.internal_coin || '',
        qtr: license.qtr || '',
      });
    } catch (error) {
      console.error('Failed to fetch license:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.status === 'Cancelled' && !formData.cancelled_date) {
      alert('Cancelled Date is required when status is Cancelled');
      return;
    }

    if (formData.status === 'Deleted' && !formData.deletion_reason.trim()) {
      alert('Deletion Reason / 刪除原因 is required when status is Deleted. Please state the reason.');
      return;
    }

    if (isEdit && !isValidPositiveInteger(licenseDbId)) {
      console.error('[LicenseForm] Update blocked — licenseDbId invalid/missing:', licenseDbId, 'URL id param:', id);
      alert('❌ Cannot update: License ID is missing or invalid. Please go back to the License list and re-open the record.');
      return;
    }
    
    console.log('Form data to save:', formData);
    try {
      if (isEdit && licenseDbId !== null) {
        // —— Debug: request payload id (the sole identifier used for UPDATE, MUST be the DB primary key from fetchLicense) ——
        console.groupCollapsed('%c[LicenseForm] UPDATE → request payload id (PUT /api/license/:id)', 'color:#b45309;font-weight:bold;');
        console.log('request payload id (= licenseDbId, from full DB lookup):', licenseDbId, 'type:', typeof licenseDbId);
        console.log('request payload id — is integer?', Number.isInteger(licenseDbId), '| positive?', licenseDbId > 0);
        console.log('licenseApi.update call signature: licenseApi.update( licenseDbId=', licenseDbId, ', formData={...} )');
        console.log('payload key fields: company_name=', formData.company_name, '| certificate_name=', formData.certificate_name, '| status=', formData.status);
        console.log('URL id param (for reference / navigation only, NEVER used for update/save):', JSON.stringify(id), 'type=', typeof id);
        console.groupEnd();
        console.log('[LicenseForm] Updating license with DB license.id =', licenseDbId);
        const response = await licenseApi.update(licenseDbId, formData);
        console.log('Update response:', response);
        alert('License updated successfully!');
      } else {
        console.log('Creating new license');
        const response = await licenseApi.create(formData);
        console.log('Create response:', response);
        alert('License created successfully!');
      }
      goBack();
    } catch (error) {
      const info = formatApiError(error, isEdit ? 'Update License' : 'Create License');
      alert(`${info.label}\n\n${info.message}`);
    }
  };

  const goBack = () => {
    const stateFrom = location.state?.from as string | undefined;
    if (stateFrom) {
      navigate(stateFrom);
      return;
    }
    const fromPage = sessionStorage.getItem('licenseList_fromPage');
    if (fromPage) {
      sessionStorage.removeItem('licenseList_fromPage');
      navigate(fromPage);
    } else {
      navigate(-1);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Certificate Name 自动补全
    if (name === 'certificate_name') {
      if (value.trim()) {
        const uniqueCerts = [...new Set(existingLicenses.map(l => l.certificate_name).filter(Boolean))];
        const filtered = uniqueCerts.filter(cert => 
          cert.toLowerCase().includes(value.toLowerCase())
        );
        setCertSuggestions(filtered);
        setShowCertSuggestions(filtered.length > 0);
      } else {
        setCertSuggestions([]);
        setShowCertSuggestions(false);
      }
    }
    
    // 在关键字段变化时触发智能填充
    if (['certificate_name', 'state', 'location'].includes(name)) {
      smartFillForm(name, value);
    }
  };

  const handleSelectCertificate = (certName: string) => {
    setFormData(prev => ({
      ...prev,
      certificate_name: certName
    }));
    setShowCertSuggestions(false);
    smartFillForm('certificate_name', certName);
  };

  const handleCompanyInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      company_name: value
    }));

    if (value.trim()) {
      const filtered = companies.filter(company =>
        company.short_name.toLowerCase().includes(value.toLowerCase()) ||
        company.company_name.toLowerCase().includes(value.toLowerCase())
      );
      setCompanySuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setCompanySuggestions([]);
      setShowSuggestions(false);
    }
    
    // 智能填充
    smartFillForm('company_name', value);
  };

  const handleSelectCompany = (company: Company) => {
    setFormData(prev => ({
      ...prev,
      company_name: company.short_name
    }));
    setShowSuggestions(false);
    setCompanySuggestions([]);
    
    // 智能填充
    smartFillForm('company_name', company.short_name);
  };

  // 复制功能
  const handleOpenCopyModal = () => {
    setSelectedLicenseToCopy(null);
    setFieldsToCopy([]);
    setLicenseSearchTerm('');
    setShowCopyModal(true);
  };

  const handleSelectLicenseToCopy = (licenseId: number) => {
    setSelectedLicenseToCopy(licenseId);
  };

  const handleFieldToggle = (fieldKey: string) => {
    setFieldsToCopy(prev => 
      prev.includes(fieldKey) 
        ? prev.filter(f => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const handleSelectAllFields = () => {
    if (fieldsToCopy.length === copyableFields.length) {
      setFieldsToCopy([]);
    } else {
      setFieldsToCopy(copyableFields.map(f => f.key));
    }
  };

  const handleCopy = () => {
    if (!selectedLicenseToCopy || fieldsToCopy.length === 0) return;
    
    const sourceLicense = existingLicenses.find(l => l.id === selectedLicenseToCopy);
    if (sourceLicense) {
      setFormData(prev => {
        const newData = { ...prev };
        fieldsToCopy.forEach(field => {
          if (sourceLicense[field] !== undefined) {
            newData[field] = sourceLicense[field];
          }
        });
        return newData;
      });
    }
    
    setShowCopyModal(false);
    setSelectedLicenseToCopy(null);
    setFieldsToCopy([]);
  };

  const handleClickOutside = () => {
    setShowSuggestions(false);
  };

  return (
    <div className="fade-in max-w-6xl mx-auto">
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={goBack}
          className="flex items-center space-x-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('action.back')}</span>
        </button>
        <h1 className="text-2xl font-bold text-slate-800">
          {isEdit ? t('license.editLicense') : t('license.newLicense')}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        {/* 复制按钮 */}
        {!isEdit && (
          <div className="flex justify-end mb-6">
            <button
              type="button"
              onClick={handleOpenCopyModal}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Copy className="w-4 h-4" />
              <span>{t('action.copyFromExisting')}</span>
            </button>
          </div>
        )}
        
        <div className="grid grid-cols-3 gap-6">
          {/* Basic Information */}
          <div className="col-span-3">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200">{t('license.basicInfo')}</h3>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.companyName')} *</label>
            {isEdit ? (
              <div className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-50">
                <span className="text-slate-800 font-medium">{formData.company_name}</span>
              </div>
            ) : (
              <>
                <input
                  ref={companyInputRef}
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleCompanyInputChange}
                  onBlur={() => setTimeout(handleClickOutside, 200)}
                  onFocus={() => {
                    if (formData.company_name.trim() && companySuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder={t('license.companyPlaceholder')}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoComplete="off"
                />
                {showSuggestions && companySuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {companySuggestions.map((company) => (
                      <div
                        key={company.short_name}
                        onClick={() => handleSelectCompany(company)}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-800">{company.short_name}</div>
                        <div className="text-sm text-slate-500">{company.company_name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.location')}</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.state')}</label>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              {stateOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.level')}</label>
            <select
              name="level"
              value={formData.level}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              {levelOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.department')}</label>
            <input
              type="text"
              name="department"
              value={formData.department}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.category')}</label>
            <MultiSelectTagInput
              name="category"
              options={categoryOptions}
              value={formData.category}
              onChange={(next) => setFormData(p => ({ ...p, category: next }))}
              placeholder="選擇或輸入 Category（Enter/逗號新增）"
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.certificateName')}</label>
            <input
              type="text"
              name="certificate_name"
              value={formData.certificate_name}
              onChange={handleChange}
              onFocus={() => {
                if (formData.certificate_name.trim()) {
                  const uniqueCerts = [...new Set(existingLicenses.map(l => l.certificate_name).filter(Boolean))];
                  const filtered = uniqueCerts.filter(cert => 
                    cert.toLowerCase().includes(formData.certificate_name.toLowerCase())
                  );
                  setCertSuggestions(filtered);
                  setShowCertSuggestions(filtered.length > 0);
                }
              }}
              ref={certInputRef}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {showCertSuggestions && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {certSuggestions.map((cert, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectCertificate(cert)}
                    className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm text-slate-700"
                  >
                    {cert}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.licenseNo')}</label>
            <input
              type="text"
              name="license_no"
              value={formData.license_no}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('license.licenseNoPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.status')}</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              {statusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {formData.status === 'Cancelled' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.cancelledDate')} *</label>
              <input
                type="date"
                name="cancelled_date"
                value={formData.cancelled_date}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.registrationDate')}</label>
            <input
              type="date"
              name="registration_date"
              value={formData.registration_date}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.renewalType')}</label>
            <select
              name="renewal_type"
              value={formData.renewal_type}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              {renewalTypeOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.expirationDate')}</label>
            <input
              type="date"
              name="expiration_date"
              value={formData.expiration_date}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.renewalDate')}</label>
            <input
              type="date"
              name="renewal_date"
              value={formData.renewal_date}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* 必須狀態 */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.requiredStatus')}</label>
            <select
              name="required_status"
              value={formData.required_status}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              <option value="required">必須有</option>
              <option value="optional_on_demand">可選，有需求就做</option>
              <option value="optional_gov_contact">可選，政府聯繫再做</option>
              <option value="not_required_gov_decide">不做，政府聯繫再決定</option>
            </select>
          </div>

          {/* Fee Information */}
          <div className="col-span-3 mt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200">{t('license.feeInfo')}</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.renewalFee')}</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input
                type="number"
                name="renewal_fee"
                value={formData.renewal_fee}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.convFee')}</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input
                type="number"
                name="conv_fee"
                value={formData.conv_fee}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.paymentMethod')}</label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              {paymentMethodOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="col-span-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.feeComment')}</label>
            <textarea
              name="fee_comment"
              value={formData.fee_comment}
              onChange={handleChange}
              rows={2}
              placeholder="Enter fee comment (optional)"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Renewal Information */}
          <div className="col-span-3 mt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200">{t('license.renewalInfo')}</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.renewalMethod')}</label>
            <select
              name="renewal_method"
              value={formData.renewal_method}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              {renewalMethodOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.renewalGuide')}</label>
            <select
              name="renewal_guide"
              value={formData.renewal_guide}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              {renewalGuideOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.renewalInfoField')}</label>
            <MultiSelectTagInput
              name="renewal_info"
              options={renewalInfoOptions}
              value={formData.renewal_info}
              onChange={(next) => setFormData(p => ({ ...p, renewal_info: next }))}
              placeholder="選擇或輸入 Renewal Info（Enter/逗號新增）"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.infoPeriod')}</label>
            <input
              type="text"
              name="info_period"
              value={formData.info_period}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.infoProvider')}</label>
            <input
              type="text"
              name="info_provider"
              value={formData.info_provider}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.noticeInvoice')}</label>
            <select
              name="notice_invoice"
              value={formData.notice_invoice}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              {noticeInvoiceOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Account Information */}
          <div className="col-span-3 mt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200">{t('license.accountInfo')}</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.website')}</label>
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.account')}</label>
            <input
              type="text"
              name="account"
              value={formData.account}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.passwordPin')}</label>
            <input
              type="text"
              name="password_pin"
              value={formData.password_pin}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.questions')}</label>
            <input
              type="text"
              name="questions"
              value={formData.questions}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Contact Information */}
          <div className="col-span-3 mt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200">{t('license.contactInfo')}</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.contactPerson')}</label>
            <input
              type="text"
              name="contact_person"
              value={formData.contact_person}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.phoneNumber')}</label>
            <input
              type="text"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.contactEmail')}</label>
            <input
              type="text"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.followUp')}</label>
            <select
              name="follow_up"
              value={formData.follow_up}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select</option>
              {followUpOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Additional Information */}
          <div className="col-span-3 mt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200">{t('license.additionalInfo')}</h3>
          </div>

          <div className="col-span-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('license.remark')}
            </label>
            <textarea
              name="remark"
              value={formData.remark}
              onChange={handleChange}
              rows={3}
              autoComplete="off"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            ></textarea>
          </div>

          {formData.status === 'Deleted' && (
            <div className="col-span-3 border border-red-200 bg-red-50/60 rounded-lg p-4 mt-2">
              <label className="block text-sm font-semibold text-red-700 mb-1.5">
                Deletion Reason
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                name="deletion_reason"
                value={formData.deletion_reason}
                onChange={handleChange}
                rows={2}
                autoComplete="off"
                placeholder="Please state the reason for marking this license as Deleted..."
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 resize-none ${!formData.deletion_reason.trim() ? 'border-red-400 focus:ring-red-500 focus:border-red-400 bg-white' : 'border-red-300 focus:ring-red-500 focus:border-red-400 bg-white'}`}
              ></textarea>
            </div>
          )}

<div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('license.qtr')}</label>
            <input
              type="text"
              name="qtr"
              value={formData.qtr}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4 mt-8">
          <button
            onClick={goBack}
            className="px-6 py-2 text-sm font-medium border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
          >
            {t('action.cancel')}
          </button>
          <button
            type="submit"
            disabled={isEdit && !isValidPositiveInteger(licenseDbId)}
            title={isEdit && !isValidPositiveInteger(licenseDbId) ? 'Cannot save: License ID is invalid — please re-open this record from the License list.' : ''}
            className={`flex items-center space-x-2 px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
              isEdit && !isValidPositiveInteger(licenseDbId)
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <Save className="w-4 h-4" />
            <span>{t('action.save')}</span>
          </button>
        </div>
      </form>

      {/* 复制模态框 */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-800">Copy from Existing License</h2>
              <button
                onClick={() => setShowCopyModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Step 1: Select License */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Step 1: Select a License to copy from</h3>
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search by company or certificate name..."
                    value={licenseSearchTerm}
                    onChange={(e) => setLicenseSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  {existingLicenses
                    .filter(license => {
                      if (!licenseSearchTerm) return true;
                      const search = licenseSearchTerm.toLowerCase();
                      return (
                        (license.company_name && license.company_name.toLowerCase().includes(search)) ||
                        (license.certificate_name && license.certificate_name.toLowerCase().includes(search)) ||
                        (license.license_no && license.license_no.toLowerCase().includes(search))
                      );
                    })
                    .map(license => (
                    <div
                      key={license.id}
                      onClick={() => handleSelectLicenseToCopy(license.id)}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-blue-50 transition-colors ${
                        selectedLicenseToCopy === license.id ? 'bg-blue-100' : ''
                      }`}
                    >
                      <div>
                        <div className="font-medium text-slate-800">{license.certificate_name}</div>
                        <div className="text-sm text-slate-500">{license.company_name}</div>
                      </div>
                      {selectedLicenseToCopy === license.id && (
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  ))}
                  {existingLicenses.filter(license => {
                    if (!licenseSearchTerm) return true;
                    const search = licenseSearchTerm.toLowerCase();
                    return (
                      (license.company_name && license.company_name.toLowerCase().includes(search)) ||
                      (license.certificate_name && license.certificate_name.toLowerCase().includes(search)) ||
                      (license.license_no && license.license_no.toLowerCase().includes(search))
                    );
                  }).length === 0 && (
                    <div className="px-4 py-6 text-center text-slate-400 text-sm">
                      No licenses found matching "{licenseSearchTerm}"
                    </div>
                  )}
                </div>
              </div>

              {/* Step 2: Select Fields */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-700">Step 2: Select fields to copy</h3>
                  <button
                    onClick={handleSelectAllFields}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {fieldsToCopy.length === copyableFields.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {copyableFields.map(field => (
                    <label
                      key={field.key}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        fieldsToCopy.includes(field.key)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={fieldsToCopy.includes(field.key)}
                        onChange={() => handleFieldToggle(field.key)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 px-6 py-4 border-t border-slate-200">
              <button
                onClick={() => setShowCopyModal(false)}
                className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                {t('action.cancel')}
              </button>
              <button
                onClick={handleCopy}
                disabled={!selectedLicenseToCopy || fieldsToCopy.length === 0}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Copy className="w-4 h-4" />
                <span>{t('action.copy')} {t('action.selected')} {t('action.fields')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}